import React, { useCallback, useEffect, useState } from "react";
import { DatabaseBackup, Download, Loader2, RefreshCw, Undo2, Trash2 } from "lucide-react";
import api, { API } from "../../api";
import { useToast } from "../../hooks/use-toast";

const mb = (b) => `${(Number(b || 0) / 1048576).toFixed(2)} MB`;
const when = (iso) => String(iso || "").slice(0, 16).replace("T", " ");

// A database without backups is one bad afternoon from losing every shop on the platform.
export const AdminBackups = () => {
  const { toast } = useToast();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.get("/admin/backups").then((r) => setD(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/backups/run");
      toast({ title: "Backup saved", description: `${data.documents} records · ${mb(data.bytes)}` });
      load();
    } catch (e) { toast({ title: "Backup failed", description: e.response?.data?.detail || "Please try again." }); }
    finally { setBusy(false); }
  };

  const restore = async (fname) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/backups/restore/${fname}`, { dryRun: true });
      const ok = window.confirm(
        `Restore ${fname}?\n\nTaken ${when(data.takenAt)}\n${data.documents} records across ` +
        `${Object.keys(data.collections).length} collections.\n\nRecords with the same id are replaced. ` +
        "Anything created since the backup is left alone. This can't be undone.");
      if (!ok) return;
      const { data: res } = await api.post(`/admin/backups/restore/${fname}`, { confirm: "RESTORE" });
      toast({ title: "Restored", description: `${res.documents} records put back.` });
      load();
    } catch (e) { toast({ title: "Restore failed", description: e.response?.data?.detail || "Please try again." }); }
    finally { setBusy(false); }
  };

  const download = (fname) => {
    const token = localStorage.getItem("lp-token");
    fetch(`${API}/admin/backups/download/${fname}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.blob() : Promise.reject(r)))
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = fname; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast({ title: "Download failed", description: "That backup may have been pruned." }));
  };

  const del = async (fname) => {
    if (!window.confirm(`Permanently delete backup ${fname}?\n\nThe file is removed from storage and can't be recovered.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/backups/${fname}`);
      toast({ title: "Backup deleted" });
      load();
    } catch (e) { toast({ title: "Delete failed", description: e.response?.data?.detail || "Please try again." }); }
    finally { setBusy(false); }
  };

  const latest = d.backups?.[0];

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-backups">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-bold text-foreground flex items-center gap-2 flex-1">
          <DatabaseBackup size={18} className="text-[#16a34a]" /> Backups
        </h2>
        <span className="text-xs text-muted-foreground">every {d.intervalHours}h · keeps {d.keepDays} days</span>
        <button onClick={runNow} disabled={busy} data-testid="run-backup-btn"
          className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-50 text-white rounded-xl px-3 py-1.5 transition-colors">
          {busy ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />} Back up now
        </button>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5 mb-4" data-testid="backup-latest">
        {latest ? `Last backup ${when(latest.createdAt)} · ${latest.documents} records · ${mb(latest.bytes)}`
                : "No backup taken yet."}
      </p>

      {(d.backups || []).length > 0 && (
        <div className="space-y-1.5 max-h-52 overflow-auto pr-1">
          {d.backups.map((b) => (
            <div key={`${b.fname}-${b.createdAt}`} className="flex items-center gap-3 text-sm border-b border-border/50 pb-1.5 last:border-0"
              data-testid={`backup-row-${b.fname}`}>
              <span className="font-mono text-xs text-foreground flex-1 truncate">{b.fname}</span>
              <span className="text-xs text-muted-foreground shrink-0">{b.documents} recs</span>
              <span className="text-xs text-muted-foreground shrink-0 w-20 text-right">{mb(b.bytes)}</span>
              <button onClick={() => download(b.fname)} data-testid={`download-${b.fname}`}
                className="text-[#16a34a] hover:underline text-xs font-semibold inline-flex items-center gap-1 shrink-0">
                <Download size={12} /> Download
              </button>
              <button onClick={() => restore(b.fname)} disabled={busy}
                data-testid={`restore-${b.fname}`}
                className="text-rose-600 hover:underline text-xs font-semibold inline-flex items-center gap-1 shrink-0 disabled:opacity-60">
                <Undo2 size={12} /> Restore
              </button>
              <button onClick={() => del(b.fname)} disabled={busy}
                data-testid={`delete-backup-${b.fname}`}
                className="text-rose-600 hover:underline text-xs font-semibold inline-flex items-center gap-1 shrink-0 disabled:opacity-60">
                <Trash2 size={12} /> Delete
              </button>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-3">
        Restore puts every record from that file back (same id = replaced) and leaves anything newer
        alone. Live traffic logs ({(d.skipped || []).join(", ")}) are left out on purpose.
      </p>
    </div>
  );
};
